import { cn } from '@/lib/utils';

type ProviderKey = 'google' | 'facebook' | 'twitter' | 'tipmi' | 'apple' | 'email' | 'phone';

const CODE_TO_PROVIDER: Record<string, ProviderKey> = {
  GG: 'google',
  FB: 'facebook',
  TWT: 'twitter',
  TM: 'tipmi',
  'google.com': 'google',
  'facebook.com': 'facebook',
  'twitter.com': 'twitter',
  'tipmi.com': 'tipmi',
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  TWITTER: 'twitter',
  TIPMI: 'tipmi',
  APPLE: 'apple',
  EMAIL: 'email',
  PHONE: 'phone',
  apple: 'apple',
  email: 'email',
  phone: 'phone',
  google: 'google',
  facebook: 'facebook',
  twitter: 'twitter',
};

/** Resolve loginMethod (code, domain, or capitalised label) to a provider key for icon lookup. */
export function resolveAuthProvider(loginMethod: string | null | undefined): ProviderKey | null {
  if (loginMethod == null || loginMethod === '') return null;
  const direct = CODE_TO_PROVIDER[loginMethod] ?? CODE_TO_PROVIDER[loginMethod.toUpperCase()];
  if (direct) return direct;
  const lower = loginMethod.toLowerCase();
  if (lower.includes('google')) return 'google';
  if (lower.includes('facebook')) return 'facebook';
  if (lower.includes('twitter') || lower === 'x') return 'twitter';
  if (lower.includes('tipmi')) return 'tipmi';
  if (lower.includes('apple')) return 'apple';
  if (lower.includes('email') || lower.includes('mail')) return 'email';
  if (lower.includes('phone') || lower.includes('mobile')) return 'phone';
  return null;
}

function GoogleIcon({ className, size }: { className?: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function FacebookIcon({ className, size }: { className?: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="#1877F2"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

function TwitterIcon({ className, size }: { className?: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  );
}

function TipmiIcon({ className, size }: { className?: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <rect width="24" height="24" rx="6" fill="#6366F1" />
      <path
        fill="white"
        d="M7.5 7.5h9v2.25h-3.38v7.5H10.88v-7.5H7.5V7.5z"
      />
    </svg>
  );
}

function AppleIcon({ className, size }: { className?: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="currentColor"
        d="M16.365 1.43c0 1.14-.417 2.2-1.25 3.03-.91.91-2.14 1.48-3.3 1.39-.05-1.1.42-2.25 1.25-3.1.9-.93 2.23-1.55 3.3-1.62zM20.5 17.16c-.55 1.25-.81 1.8-1.52 2.9-.99 1.54-2.39 3.46-4.12 3.48-1.54.02-1.94-.99-4.04-.98-2.1.01-2.54 1-4.08.98-1.73-.02-3.05-1.75-4.04-3.29C.88 17.2-.58 12.13 1.7 8.68c1.14-1.73 2.95-2.82 4.64-2.82 1.73 0 2.82 1 4.25 1 1.38 0 2.22-1 4.24-1 1.5 0 3.09.82 4.22 2.23-3.71 2.03-3.11 7.33.45 9.07z"
      />
    </svg>
  );
}

function EmailIcon({ className, size }: { className?: string; size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function PhoneIcon({ className, size }: { className?: string; size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M11 18h2" />
    </svg>
  );
}

const ICONS: Record<ProviderKey, typeof GoogleIcon> = {
  google: GoogleIcon,
  facebook: FacebookIcon,
  twitter: TwitterIcon,
  tipmi: TipmiIcon,
  apple: AppleIcon,
  email: EmailIcon,
  phone: PhoneIcon,
};

/** CMP parity: provider icon beside login method label (UserManagementScreen.kt BaseCellWithIcon). */
export function AuthProviderIcon({
  loginMethod,
  className,
  size = 20,
}: {
  loginMethod: string | null | undefined;
  className?: string;
  size?: number;
}) {
  const provider = resolveAuthProvider(loginMethod);
  if (!provider) return null;
  const Icon = ICONS[provider];
  const ink =
    provider === 'twitter' ||
    provider === 'apple' ||
    provider === 'email' ||
    provider === 'phone';
  return (
    <Icon
      size={size}
      className={cn('shrink-0', ink && 'text-foreground', className)}
    />
  );
}
